import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FolderOpen, Trash2, Upload, Loader2 } from 'lucide-react';
import { getCvStats, uploadCvs, clearCvs } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

function CvUploadSection({ onCountChange }) {
  const [count, setCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);

  const refreshStats = useCallback(async () => {
    try {
      const data = await getCvStats();
      setCount(data.count ?? 0);
      if (onCountChange) {
        onCountChange(data.count ?? 0);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load CV count');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    setMessage(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Select one or more PDF files to upload.');
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);

    try {
      const result = await uploadCvs(selectedFiles);
      const savedCount = result.saved?.length ?? 0;
      const skippedCount = result.skipped?.length ?? 0;
      setCount(result.count ?? 0);
      if (onCountChange) {
        onCountChange(result.count ?? 0);
      }

      let msg = `Uploaded ${savedCount} PDF${savedCount === 1 ? '' : 's'}.`;
      if (skippedCount > 0) {
        msg += ` Skipped ${skippedCount} file${skippedCount === 1 ? '' : 's'} (non-PDF or invalid).`;
      }
      setMessage(msg);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClearConfirm = async () => {
    setClearing(true);
    setError(null);
    setMessage(null);

    try {
      const result = await clearCvs();
      const deletedCount = result.deleted?.length ?? 0;
      setCount(0);
      if (onCountChange) {
        onCountChange(0);
      }
      setMessage(`Cleared ${deletedCount} CV${deletedCount === 1 ? '' : 's'} from data/.`);
      setConfirmOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to clear CVs');
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Card className="shadow-xl border-0 animate-fade-in mb-6">
        <CardHeader className="space-y-1 pb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-xl">CV Files</CardTitle>
            </div>
            <Badge variant="secondary" className="text-sm px-3 py-1">
              {loading ? '…' : `${count} CV${count === 1 ? '' : 's'} in data/`}
            </Badge>
          </div>
          <CardDescription>
            Upload multiple PDF resumes at once. Clear existing files before starting a new batch.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileChange}
              className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
            />
            {selectedFiles.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'} selected
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>

            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={clearing || loading || count === 0}
              className="gap-2"
            >
              {clearing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Clear Data
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear all CVs?</DialogTitle>
            <DialogDescription>
              This will permanently delete all {count} PDF file{count === 1 ? '' : 's'} from the{' '}
              <code className="bg-muted px-1 rounded text-sm">data/</code> folder. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={clearing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleClearConfirm}
              disabled={clearing}
              className="gap-2"
            >
              {clearing && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CvUploadSection;
